import { useState } from 'react';
import type { FormEvent } from 'react';
import { genres, siteStats, movieCatalog } from '@/mocks/movies';
import type { Movie } from '@/pages/home/types';

interface HeroProps {
  onSearch: (value: string) => void;
  onPickGenre: (genre: string) => void;
}

export default function Hero({ onSearch, onPickGenre }: HeroProps) {
  const [value, setValue] = useState('');
  const hotTitles = (movieCatalog as Movie[]).filter((m) => m.featured).slice(0, 3);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSearch(value);
  };

  return (
    <section id="top" className="relative overflow-hidden bg-gradient-to-b from-background-100 via-background-50 to-background-50">
      <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-12 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="flex flex-col gap-6">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs font-medium text-foreground-700">
            <i className="ri-flashlight-line text-accent-600"></i>
            片库资源整理 · 每周持续更新
          </span>

          <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
            555电影电影大全
            <span className="mt-2 block text-xl font-bold text-foreground-700 md:text-2xl">
              电视剧 · 最新电影 · 免费电影一站选
            </span>
          </h1>

          <p className="max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里整理 555电影 的片库资源，涵盖电影大全、电视剧、最新电影和免费电影，
            并提供好看的电影推荐。无论你想看热门新片还是经典老剧，都能在片库中快速找到目标内容。
          </p>

          <form onSubmit={handleSubmit} className="flex w-full max-w-xl flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-lg"></i>
              </span>
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="输入片名、类型或关键词，如：科幻、悬疑"
                aria-label="搜索片库"
                className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="submit"
              className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-eye-line text-lg"></i>
              搜索片库
            </button>
          </form>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门类型：</span>
            {genres.slice(1, 7).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => onPickGenre(g)}
                className="cursor-pointer rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                {g}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4 border-t border-background-200 pt-6 sm:grid-cols-4">
            {siteStats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1">
                <span className="font-heading text-xl font-black text-accent-600 md:text-2xl">
                  {stat.value}
                </span>
                <span className="text-xs text-foreground-500">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="relative w-full overflow-hidden rounded-lg border border-background-200">
            <div className="h-[360px] w-full md:h-[520px]">
              <img
                src="https://readdy.ai/api/search-image?query=cozy%20modern%20home%20cinema%20room%20with%20warm%20amber%20lighting%2C%20a%20large%20glowing%20screen%2C%20plush%20seats%20and%20soft%20golden%20ambient%20glow%2C%20cinematic%20atmosphere%20with%20crimson%20accents%2C%20dark%20elegant%20background%2C%20editorial%20photography&width=1000&height=1200&seq=hero-cinema-01&orientation=portrait"
                alt="温暖的居家影院观影场景"
                title="555电影片库观影场景"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-x-0 bottom-0 flex flex-col gap-1 bg-gradient-to-t from-foreground-950/85 to-transparent p-5">
              <span className="text-xs text-background-200">本周精选</span>
              <span className="font-heading text-lg font-bold text-background-50">
                深夜观影片单 · 12 部高分好片
              </span>
            </div>
          </div>

          <div className="absolute -bottom-6 -left-4 hidden w-56 rounded-lg border border-background-200 bg-background-50 p-4 sm:block">
            <span className="text-xs text-foreground-500">正在热播</span>
            <div className="mt-3 flex flex-col gap-3">
              {hotTitles.map((m) => (
                <div key={m.id} className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-movie-line text-lg"></i>
                  </span>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground-900">{m.title}</span>
                    <span className="text-xs text-foreground-500">{m.genre} · {m.rating} 分</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}